import axios, { type AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

// List envelope returned by every paginated endpoint in the new Hono backend:
// GET /api/<resource> → { data, total, limit, offset }.
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

// Shared axios instance for all feature `.service.ts` files.
// Auth is cookie-based (Better Auth) — `withCredentials: true` sends the
// session cookie on every request. The session cookie is set by the backend
// during sign-in via /api/auth/sign-in/email.
export const api = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || 'https://admin-api.stgeorgeenglish.com'}/api`,
  timeout: 10000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Path of the Better Auth session check. We never want to show "session
// expired" if THIS call returns 401 — that's the normal flow for an
// unauthenticated visitor hitting a protected page.
const AUTH_SESSION_PATH = '/api/auth/get-session';

let sessionExpiredToastShown = false;

api.interceptors.response.use(
  (response) => {
    return response.data;
  },
  (error) => {
    if (error.response?.status === 401) {
      const onLoginRoute = window.location.pathname === '/login';
      const requestUrl: string | undefined = error.config?.url;
      const isSessionProbe = requestUrl?.includes(AUTH_SESSION_PATH);

      if (!onLoginRoute && !isSessionProbe && !sessionExpiredToastShown) {
        sessionExpiredToastShown = true;
        toast.error('Tu sesión expiró. Por favor inicia sesión nuevamente.');
        // Defer the redirect briefly so the toast renders before unmount.
        setTimeout(() => {
          window.location.href = '/login';
        }, 600);
      }
    }
    // Backend errors come as { error: msg } (see app.onError); some as { message }.
    // Normalize to an Error so every `error.message` handler shows the real text.
    const data = error.response?.data;
    const fromData =
      typeof data === 'string' ? data : (data?.error ?? data?.message);
    return Promise.reject(
      new Error(fromData || error.message || 'Ocurrió un error inesperado'),
    );
  },
);

// The response interceptor above returns `response.data`, so every call already
// resolves to the HTTP BODY (not an AxiosResponse). This typed facade makes that
// explicit and centralizes the one cast: `http.get<T>()` → Promise<T> (the body).
//
// Usage in services:
//   list    → http.get<PaginatedResponse<X>>('/x', { params })   // keep envelope
//   single  → http.get<{ data: X }>('/x/:id').then((r) => r.data) // unwrap to entity
export const http = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    api.get(url, config) as unknown as Promise<T>,
  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.post(url, data, config) as unknown as Promise<T>,
  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.put(url, data, config) as unknown as Promise<T>,
  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    api.patch(url, data, config) as unknown as Promise<T>,
  delete: <T = void>(url: string, config?: AxiosRequestConfig) =>
    api.delete(url, config) as unknown as Promise<T>,
};
